import { create } from 'zustand';

/**
 * Lựa chọn của người dùng về thanh điều hướng, **có nhớ qua lần tải sau**.
 *
 * Spec §10.4 ghi trạng thái gập là "component-local, **not** persisted" — đó là
 * mô tả bản gốc, không phải điều đáng giữ: cây có 12 nhóm và ~59 mục, nên mỗi
 * lần F5 người dùng lại phải gập lại đúng những nhóm họ vừa gập. Đây là sai
 * lệch có chủ đích, ghi ở ADR-UI-0009.
 *
 * Chỉ nhớ **hình dạng** thanh điều hướng. Từ khoá tìm kiếm **không** nhớ: mở
 * lại ứng dụng mà thấy cây đang bị lọc bởi chữ gõ hôm qua thì trông như hỏng.
 */

const KHOA = 'foxai-nav';

/**
 * Nhóm nào đang đóng/mở tay, mục cha nào đang đóng, thanh có gập hay không.
 *
 * Phải lưu **cả hai** danh sách nhóm: `nhomDong` và `nhomMo` là hai lựa chọn
 * khác nhau, không phải phần bù của nhau. Nhóm không có trong danh sách nào là
 * "chưa chạm" và đi theo luật mặc định (chỉ mở nhóm đang xem). Lưu thiếu
 * `nhomMo` thì nhóm người dùng tự mở sẽ đóng lại sau khi tải lại trang.
 */
interface NavLuu {
  gap: boolean;
  nhomDong: string[];
  nhomMo: string[];
  chaDong: string[];
}

const RONG: NavLuu = { gap: false, nhomDong: [], nhomMo: [], chaDong: [] };

function doc(): NavLuu {
  try {
    const raw = localStorage.getItem(KHOA);
    if (!raw) return RONG;
    const v = JSON.parse(raw) as Partial<NavLuu>;
    // Đọc phòng vệ từng trường: dữ liệu cũ hoặc bị sửa tay không được làm
    // trắng cả thanh điều hướng.
    return {
      gap: typeof v.gap === 'boolean' ? v.gap : false,
      nhomDong: Array.isArray(v.nhomDong) ? v.nhomDong.filter((x) => typeof x === 'string') : [],
      nhomMo: Array.isArray(v.nhomMo) ? v.nhomMo.filter((x) => typeof x === 'string') : [],
      chaDong: Array.isArray(v.chaDong) ? v.chaDong.filter((x) => typeof x === 'string') : [],
    };
  } catch {
    // Trình duyệt chặn localStorage hoặc JSON hỏng: chạy với mặc định.
    return RONG;
  }
}

function ghi(s: NavLuu): void {
  try {
    localStorage.setItem(KHOA, JSON.stringify(s));
  } catch {
    // Chặn ghi thì chỉ mất khả năng nhớ, không được làm gãy thao tác đang làm.
  }
}

interface NavPrefsState {
  /** Nhóm người dùng đã **tự tay** đóng. */
  nhomDong: ReadonlySet<string>;
  /** Nhóm người dùng đã **tự tay** mở — thắng luật "chỉ mở nhóm đang xem". */
  nhomMo: ReadonlySet<string>;
  chaDong: ReadonlySet<string>;
  datNhom: (title: string, mo: boolean) => void;
  datCha: (label: string, mo: boolean) => void;
  /**
   * Mở hết hoặc gập hết, một lượt.
   *
   * Phải nhận **danh sách đầy đủ** tên nhóm và khoá cụm đang có, chứ không tự
   * suy: kho này không biết cây điều hướng (cây còn phụ thuộc quyền của người
   * đăng nhập và danh sách bot nạp lúc chạy). Nhóm "chưa chạm" đi theo luật
   * mặc định, nên muốn gập **hết** thì phải ghi tên từng nhóm vào `nhomDong` —
   * để trống thì nhóm chứa route đang xem vẫn tự mở và nút trông như hỏng.
   */
  datTatCa: (mo: boolean, tenNhom: readonly string[], khoaCha: readonly string[]) => void;
}

const banDau = doc();

/**
 * Nhóm có ba trạng thái, không phải hai: **chưa chạm** (đi theo luật mặc định —
 * chỉ mở nhóm chứa route đang xem), **đã mở tay**, **đã đóng tay**. Thiếu trạng
 * thái "chưa chạm" thì không thể vừa mặc định gập hết vừa tôn trọng lựa chọn
 * của người dùng: mọi nhóm sẽ trông như đã bị người dùng đóng.
 */
export const useNavPrefs = create<NavPrefsState>((set, get) => ({
  nhomDong: new Set(banDau.nhomDong),
  nhomMo: new Set(banDau.nhomMo),
  chaDong: new Set(banDau.chaDong),

  datNhom: (title, mo) =>
    set((s) => {
      const dong = new Set(s.nhomDong);
      const moTay = new Set(s.nhomMo);
      if (mo) {
        dong.delete(title);
        moTay.add(title);
      } else {
        dong.add(title);
        moTay.delete(title);
      }
      ghi({
        gap: gapHienTai,
        nhomDong: [...dong],
        nhomMo: [...moTay],
        chaDong: [...get().chaDong],
      });
      return { nhomDong: dong, nhomMo: moTay };
    }),

  datTatCa: (mo, tenNhom, khoaChaDs) =>
    set(() => {
      // Mở hết: xoá sạch hai danh sách "đóng", và ghi mọi nhóm vào `nhomMo` để
      // thắng luật mặc định (chỉ mở nhóm đang xem).
      // Gập hết: ngược lại.
      const nhomDong = mo ? new Set<string>() : new Set(tenNhom);
      const nhomMo = mo ? new Set(tenNhom) : new Set<string>();
      const chaDong = mo ? new Set<string>() : new Set(khoaChaDs);
      ghi({
        gap: gapHienTai,
        nhomDong: [...nhomDong],
        nhomMo: [...nhomMo],
        chaDong: [...chaDong],
      });
      return { nhomDong, nhomMo, chaDong };
    }),

  datCha: (label, mo) =>
    set((s) => {
      const dong = new Set(s.chaDong);
      if (mo) dong.delete(label);
      else dong.add(label);
      ghi({
        gap: gapHienTai,
        nhomDong: [...get().nhomDong],
        nhomMo: [...get().nhomMo],
        chaDong: [...dong],
      });
      return { chaDong: dong };
    }),
}));

/** Giá trị gập đọc từ `localStorage` lúc khởi tạo, cho `useUiStore` dùng. */
export const GAP_BAN_DAU = banDau.gap;

/**
 * Trạng thái gập của thanh sống ở `useUiStore` (đã có nhiều chỗ đọc nó) nhưng
 * ghi chung một bản ghi `localStorage` với các lựa chọn ở đây. Giữ bản sao mới
 * nhất để lần ghi nào cũng mang đủ ba trường — ghi thiếu thì thao tác gập nhóm
 * sẽ xoá mất trạng thái gập thanh.
 */
let gapHienTai = banDau.gap;

/** `useUiStore` gọi khi người dùng gập/mở thanh. */
export function luuGap(gap: boolean): void {
  gapHienTai = gap;
  const s = useNavPrefs.getState();
  ghi({
    gap,
    nhomDong: [...s.nhomDong],
    nhomMo: [...s.nhomMo],
    chaDong: [...s.chaDong],
  });
}
